import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import usePageMeta from '@/hooks/usePageMeta';
import { siteCategories } from '@/mocks/navigation';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dah8c9bp14a1h3mr6f6g';
const MAX_INTRO = 500;

const criteria = [
  { icon: 'ri-checkbox-circle-line', title: '内容合规', desc: '不涉及违法信息，站点内容方向清晰可描述。' },
  { icon: 'ri-links-line', title: '可稳定访问', desc: '能正常打开、不需要特殊手段才能访问。' },
  { icon: 'ri-layout-grid-line', title: '归属明确', desc: '能对应到我们已开设的十个分类之一。' },
  { icon: 'ri-time-line', title: '持续更新', desc: '近期仍有内容更新，不是长期停更的空站。' },
];

interface FormState {
  status: 'idle' | 'submitting' | 'success' | 'error';
  message: string;
}

export default function Submit() {
  const [state, setState] = useState<FormState>({ status: 'idle', message: '' });
  const [introLength, setIntroLength] = useState(0);

  const options = useMemo(() => {
    const base = (typeof __BASE_PATH__ === 'string' ? __BASE_PATH__ : '/').replace(/\/$/, '');
    const origin = window.location.origin;
    const url = `${origin}${base}/submit`;
    return {
      title: '提交站点收录 - 申请加入爆料导航网站目录 | 爆料导航网',
      description:
        '通过收录申请表单，把你常用或自己运营的合规站点提交给爆料导航网。审核一般需要三到五个工作日，收录不收取任何费用。',
      keywords: '爆料导航收录,提交站点,网站收录申请,爆料导航网站目录',
      path: '/submit',
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            name: '提交站点收录',
            url,
            inLanguage: 'zh-CN',
            description: '申请把站点加入爆料导航网站目录的入口页面。',
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}${base}/` },
              { '@type': 'ListItem', position: 2, name: '提交站点收录', item: url },
            ],
          },
        ],
      },
    };
  }, []);

  usePageMeta(options);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setState({ status: 'success', message: '申请已提交，我们会尽快完成初审。' });
      form.reset();
      setIntroLength(0);
      return;
    }

    const siteName = String(formData.get('site_name') ?? '').trim();
    const siteUrl = String(formData.get('site_url') ?? '').trim();
    const category = String(formData.get('category') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const description = String(formData.get('description') ?? '').trim();

    if (!siteName || !siteUrl || !category || !email || !description) {
      setState({ status: 'error', message: '请把站点名称、站点地址、所属分类、联系邮箱和站点简介填写完整。' });
      return;
    }
    if (!/^https?:\/\/.+\..+/i.test(siteUrl)) {
      setState({ status: 'error', message: '站点地址需要以 http:// 或 https:// 开头，请检查后重新填写。' });
      return;
    }
    if (description.length > MAX_INTRO) {
      setState({ status: 'error', message: `站点简介请控制在 ${MAX_INTRO} 字以内。` });
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = typeof value === 'string' ? value.trim() : '';
      if (text) {
        payload.append(key, text);
      }
    });

    setState({ status: 'submitting', message: '' });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMessage === 'string' && serverMessage.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setState({ status: 'success', message: '申请已提交，我们会在三到五个工作日内完成初审。' });
        form.reset();
        setIntroLength(0);
      } else {
        setState({
          status: 'error',
          message: serverMessage || '提交没有成功，请稍后重试，或直接发邮件联系我们。',
        });
      }
    } catch {
      setState({ status: 'error', message: '网络似乎不太稳定，提交没有成功，请稍后重试。' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-6 lg:px-10 pt-24 md:pt-32 pb-10 bg-background-100">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '提交站点收录' }]} />
          <h1 className="mt-6 max-w-3xl font-heading text-3xl md:text-4xl font-black text-foreground-950">
            提交站点收录
          </h1>
          <p className="mt-5 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-700">
            把你常用、或者自己运营的站点告诉我们。只要内容合规、能稳定访问，并且属于已开设的分类方向，就有机会进入网站目录。
            收录完全免费，也不提供付费置顶。
          </p>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-16 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7">
              {state.status === 'success' ? (
                <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-6 md:p-8">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-500 text-background-50 text-2xl">
                    <i className="ri-check-line" aria-hidden="true"></i>
                  </span>
                  <h2 className="mt-4 font-heading text-xl font-bold text-secondary-900">提交成功</h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{state.message}</p>
                  <div className="mt-5 flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => setState({ status: 'idle', message: '' })}
                      className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 font-label text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
                    >
                      <i className="ri-add-line" aria-hidden="true"></i>
                      再提交一个站点
                    </button>
                    <Link
                      to="/#directory"
                      className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md border border-background-300 bg-background-50 font-label text-sm whitespace-nowrap text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors"
                    >
                      返回站点目录
                    </Link>
                  </div>
                </div>
              ) : (
                <form
                  id="site-submit-form"
                  data-readdy-form=""
                  onSubmit={handleSubmit}
                  noValidate
                  className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7"
                >
                  <h2 className="font-heading text-lg font-bold text-foreground-950">收录申请信息</h2>
                  <p className="mt-2 text-xs text-foreground-500">带 * 的项目为必填项。</p>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="sm:col-span-1">
                      <label htmlFor="site_name" className="font-label text-sm text-foreground-800">
                        站点名称 *
                      </label>
                      <input
                        id="site_name"
                        name="site_name"
                        type="text"
                        required
                        maxLength={60}
                        placeholder="例如：车主吐槽站"
                        className="mt-2 w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>

                    <div className="sm:col-span-1">
                      <label htmlFor="site_url" className="font-label text-sm text-foreground-800">
                        站点地址 *
                      </label>
                      <input
                        id="site_url"
                        name="site_url"
                        type="url"
                        required
                        placeholder="https://"
                        className="mt-2 w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>

                    <div className="sm:col-span-1">
                      <label htmlFor="category" className="font-label text-sm text-foreground-800">
                        所属分类 *
                      </label>
                      <select
                        id="category"
                        name="category"
                        required
                        defaultValue=""
                        className="mt-2 w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
                      >
                        <option value="" disabled>
                          请选择一个分类
                        </option>
                        {siteCategories.map((category) => (
                          <option key={category.id} value={category.name}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-1">
                      <label htmlFor="email" className="font-label text-sm text-foreground-800">
                        联系邮箱 *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="用于接收审核结果"
                        className="mt-2 w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="description" className="font-label text-sm text-foreground-800">
                        站点简介 *
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        required
                        rows={5}
                        maxLength={MAX_INTRO}
                        onChange={(event) => setIntroLength(event.target.value.length)}
                        placeholder="用一两句话说明站点收录什么内容、更新频率如何，方便我们判断分类归属。"
                        className="mt-2 w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm leading-relaxed text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 resize-none"
                      />
                      <p className="mt-1.5 text-right font-label text-[11px] text-foreground-500">
                        {introLength} / {MAX_INTRO}
                      </p>
                    </div>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="alt-field-guard"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {state.status === 'error' && state.message && (
                    <p
                      role="alert"
                      className="mt-5 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-900"
                    >
                      <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
                      {state.message}
                    </p>
                  )}

                  <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                    <button
                      type="submit"
                      disabled={state.status === 'submitting'}
                      className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-md bg-primary-500 text-background-50 font-label text-sm whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {state.status === 'submitting' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                          正在提交
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" aria-hidden="true"></i>
                          提交收录申请
                        </>
                      )}
                    </button>
                    <p className="text-xs leading-relaxed text-foreground-500">
                      提交即表示同意我们核对站点公开信息，仅用于收录审核。
                    </p>
                  </div>
                </form>
              )}
            </div>

            <aside className="lg:col-span-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">收录标准</h2>
                <ul className="mt-4 space-y-4">
                  {criteria.map((item) => (
                    <li key={item.title} className="flex gap-3">
                      <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 text-lg">
                        <i className={item.icon} aria-hidden="true"></i>
                      </span>
                      <div>
                        <h3 className="font-heading text-sm font-bold text-foreground-950">{item.title}</h3>
                        <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
                <h2 className="font-heading text-base font-bold text-primary-900">审核流程</h2>
                <ol className="mt-4 space-y-3">
                  <li className="flex items-center gap-3 font-label text-sm text-foreground-700">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs">1</span>
                    提交基本信息与分类归属
                  </li>
                  <li className="flex items-center gap-3 font-label text-sm text-foreground-700">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs">2</span>
                    编辑核对内容与可访问性
                  </li>
                  <li className="flex items-center gap-3 font-label text-sm text-foreground-700">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs">3</span>
                    通过后进入对应分类目录
                  </li>
                </ol>
                <p className="mt-4 flex items-center gap-2 text-xs text-foreground-600">
                  <i className="ri-mail-line" aria-hidden="true"></i>
                  也可以直接发邮件至 contact@baoliao-nav.com
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}